import { useCallback, useMemo, useState } from 'react'

import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import ConfirmationModal from 'components/ConfirmationModal'
import { useGlobalState } from 'contexts/global-state'
import { ApiService } from 'services/api'
import { CacheService } from 'services/cache'

type UpdateInvoiceStatusButtonProps = {
  reservation?: Reservation
  onLoading?: (value: boolean) => void
}

type UpdateInvoiceStatusModalProps = {
  opened: boolean
  reservation_code: string
  onClose: () => void
  onConfim: () => void
  onCancel: () => void
}

const UpdateInvoiceStatusModal = ({
  onCancel,
  onClose,
  onConfim,
  opened,
  reservation_code,
}: UpdateInvoiceStatusModalProps) => {
  return (
    <ConfirmationModal
      title="Emissão de nota fiscal"
      opened={opened}
      onClose={onClose}
      onConfim={onConfim}
      onCancel={onCancel}
    >
      <p>
        Deseja confirmar a emissão de nota fiscal para a reserva{' '}
        <strong>{reservation_code}</strong>
      </p>
    </ConfirmationModal>
  )
}

export const UpdateInvoiceStatusButton = ({
  reservation,
  onLoading,
}: UpdateInvoiceStatusButtonProps) => {
  const { openInfoToast, openErrorToast, openSuccessToast } = useGlobalState()

  const [updateInvoiceStatusModal, setUpdateInvoiceStatusModal] =
    useState(false)

  const handleUpdateStatusInvoice = useCallback(async () => {
    onLoading?.(true)
    if (!reservation) {
      return
    }
    const { id } = reservation
    try {
      const responseUpdateStatusInvoice =
        await ApiService.Reservations.updateInvoiceStatus({
          id: id.toString(),
        })
      CacheService.queryClient.invalidateQueries()
      if (!responseUpdateStatusInvoice.success) {
        setUpdateInvoiceStatusModal(false)
        openInfoToast({
          message: responseUpdateStatusInvoice.message,
        })
        onLoading?.(false)
        return
      }
      setUpdateInvoiceStatusModal(false)
      openSuccessToast({
        message: 'Processo de emissão de nota fiscal iniciado com sucesso',
      })
      onLoading?.(false)
    } catch (error: unknown) {
      openErrorToast({
        message: (error as ApiError)?.message || (error as string),
      })
    }
  }, [reservation, onLoading, openErrorToast, openInfoToast, openSuccessToast])

  const isDisabled = useMemo(() => {
    if (!reservation) {
      return true
    }

    if (reservation.situation !== 'finished') {
      return true
    }

    if (reservation.invoice_status) {
      return true
    }
  }, [reservation])

  if (!reservation) {
    return null
  }

  return (
    <>
      <Box sx={{ m: 1 }}>
        <Button
          color="info"
          variant="contained"
          onClick={() => {
            setUpdateInvoiceStatusModal(true)
          }}
          disabled={isDisabled}
        >
          Emissão de nota fiscal
        </Button>
      </Box>

      <UpdateInvoiceStatusModal
        reservation_code={reservation.code || ''}
        onCancel={() => setUpdateInvoiceStatusModal(false)}
        onClose={() => setUpdateInvoiceStatusModal(false)}
        onConfim={handleUpdateStatusInvoice}
        opened={updateInvoiceStatusModal}
      />
    </>
  )
}
